import React from "react";
import { motion } from "framer-motion";
import { Users, Target, TrendingUp } from "lucide-react";

const WhyItWorks = ({ handleOpenModal }) => {
  const waitlistUrl = "https://portal.selfsync.ai/sign-up";

  const benefits = [
    {
      icon: <Target className="w-12 h-12 text-purple-600" />,
      title: "Go Beyond Qualifications",
      description: "While others test what people know, we reveal who they really are. Our AI uncovers deep personality traits that predict actual job performance.",
      stat: "99% accuracy"
    },
    {
      icon: <Users className="w-12 h-12 text-cyan-500" />,
      title: "Human Psychology, AI Speed",
      description: "Get insights that would take psychologists hours to uncover - delivered in minutes. No bias, no guesswork, just pure data-driven hiring.",
      stat: "30-minute test"
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-green-500" />,
      title: "Proven ROI Impact",
      description: "Companies using our assessments see 3x better retention and 25% higher productivity. Stop hiring mistakes before they happen.",
      stat: "3x retention"
    }
  ];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="py-12 px-6 md:px-14 lg:px-20 bg-gradient-to-br from-purple-50 to-cyan-50"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.2 } },
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Why{" "}
            <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
              Smart Companies
            </span>{" "}
            Choose SelfSync
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stop gambling on gut feelings. Start hiring with scientific precision.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
  {benefits.map((benefit, index) => (
    <motion.div
      key={index}
      className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center relative overflow-hidden group flex flex-col" // Added flex-col
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
    >
      {/* Background gradient on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      <div className="relative z-10 flex flex-col h-full">
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            {benefit.icon}
          </div>
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">
          {benefit.title}
        </h3>

        <p className="text-gray-600 leading-relaxed mb-6">
          {benefit.description}
        </p>

        {/* Push this to the bottom */}
        <div className="mt-auto inline-block bg-gradient-to-r from-purple-100 to-cyan-100 text-purple-700 font-bold px-4 py-2 rounded-full text-sm">
          {benefit.stat}
        </div>
      </div>
    </motion.div>
  ))}
</div>


        {/* CTA Section */}
        <motion.div
          className="text-center bg-white rounded-2xl p-8 shadow-lg"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
           Still Guessing Who to Hire?
          </h3>
          <p className="text-gray-600 mb-6">
           260+ companies have already upgraded. Ready to join them?
          </p>
          <motion.button
            className="custom-button text-white font-bold px-6 py-2 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg text-base md:text-lg transform hover:scale-105"
            onClick={() => window.open(waitlistUrl, "_blank")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Free Assessment
          </motion.button>
          <p className="text-sm text-gray-500 mt-3">
            Takes 30 minutes • No credit card required • Instant results
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default WhyItWorks;
